import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { isPlatformAdmin } from '../lib/roleHelpers'
import { mutate } from '../lib/dbMutate'
import { Shield, ArrowLeft, Users, UserCog, RefreshCw, Pencil, X, Check, Building2, ChevronDown, ChevronRight, Plus, Trash2, Settings } from 'lucide-react'
import { validateDisplayName, validateCallsign } from '../lib/validation'

// ── CIPHER STYLE TOKENS ──────────────────────────────────────
const C = {
  page: {
    minHeight: '100vh',
    background: '#000000',
    color: '#00ff66',
    fontFamily: "'JetBrains Mono', monospace",
    textShadow: '0 0 4px rgba(0,255,102,.5)',
    position: 'relative',
  },
  nav: {
    background: '#0a140a',
    borderBottom: '1px solid rgba(0,255,102,.18)',
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    zIndex: 10,
  },
  display: { fontFamily: "'VT323', 'Share Tech Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  phos: { color: '#00ff66' },
  dim: { color: '#4aaa5a' },
  deep: { color: '#2a6a3a' },
  alert: { color: '#ff2a2a', textShadow: '0 0 4px rgba(255,42,42,.5)' },
  amber: { color: '#ffcc33', textShadow: '0 0 4px rgba(255,204,51,.5)' },
  panel: {
    background: 'rgba(0, 20, 8, .4)',
    border: '1px solid rgba(0,255,102,.18)',
    boxShadow: '0 0 6px rgba(0,255,102,.08)',
    position: 'relative',
  },
  panelBright: {
    background: 'rgba(0, 20, 8, .4)',
    border: '1px solid #00ff66',
    boxShadow: '0 0 12px rgba(0,255,102,.15), inset 0 0 20px rgba(0,255,102,.04)',
    position: 'relative',
  },
  input: {
    background: 'rgba(0, 20, 8, .6)',
    color: '#00ff66',
    border: '1px solid rgba(0,255,102,.3)',
    padding: '6px 12px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '13px',
    outline: 'none',
    textShadow: '0 0 4px rgba(0,255,102,.3)',
  },
  select: {
    background: '#0a140a',
    color: '#00ff66',
    border: '1px solid rgba(0,255,102,.3)',
    padding: '6px 12px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '13px',
    outline: 'none',
  },
  btnPrimary: {
    fontFamily: "'VT323', monospace",
    fontSize: '14px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    background: '#00ff66',
    color: '#000000',
    border: 'none',
    padding: '6px 14px',
    cursor: 'pointer',
    boxShadow: '0 0 6px #00ff66',
    textShadow: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  btnSecondary: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
    background: 'rgba(0, 20, 8, .4)',
    color: '#4aaa5a',
    border: '1px solid rgba(0,255,102,.18)',
    padding: '6px 14px',
    cursor: 'pointer',
    textShadow: '0 0 4px rgba(0,255,102,.3)',
  },
  btnDanger: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
    background: 'rgba(40, 8, 8, .4)',
    color: '#ff2a2a',
    border: '1px solid rgba(255,42,42,.18)',
    padding: '6px 14px',
    cursor: 'pointer',
    textShadow: '0 0 4px rgba(255,42,42,.3)',
  },
  btnNav: {
    color: '#4aaa5a',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    textShadow: '0 0 4px rgba(0,255,102,.3)',
  },
  tag: { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px', textShadow: 'none' },
}

const roleTag = (role) => {
  switch(role) {
    case 'm55_admin': return { background: 'rgba(154,102,255,.12)', color: '#b380ff', border: '1px solid rgba(154,102,255,.3)' }
    case 'gm': return { background: 'rgba(255,204,51,.08)', color: '#ffcc33', border: '1px solid rgba(255,204,51,.3)' }
    default: return { background: 'rgba(0,255,102,.06)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.15)' }
  }
}

const orgRoleTag = (role) => {
  switch(role) {
    case 'org_owner':  return { background: 'rgba(255,204,51,.08)', color: '#ffcc33', border: '1px solid rgba(255,204,51,.3)' }
    case 'org_admin':  return { background: 'rgba(154,102,255,.12)', color: '#b380ff', border: '1px solid rgba(154,102,255,.3)' }
    case 'org_gm':     return { background: 'rgba(77,214,255,.10)', color: '#4dd6ff', border: '1px solid rgba(77,214,255,.3)' }
    case 'org_member': return { background: 'rgba(0,255,102,.06)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.15)' }
    default:           return { background: 'rgba(0,255,102,.06)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.15)' }
  }
}

const ORG_ROLE_OPTIONS = ['org_owner','org_admin','org_gm','org_member']

const roleLabel = (role) => {
  switch(role) {
    case 'm55_admin': return 'M55 ADMIN'
    case 'gm': return 'GM'
    case 'co_gm': return 'CO-GM'
    case 'player': return 'PLAYER'
    default: return role?.toUpperCase()
  }
}

function Brackets({ children, style = {}, className = '', bright = false }) {
  const ps = bright ? C.panelBright : C.panel
  const color = bright ? '#9aff9a' : '#4aaa5a'
  return (
    <div className={className} style={{ ...ps, ...style }}>
      <div style={{ position:'absolute',top:-1,left:-1,width:8,height:8,borderTop:`2px solid ${color}`,borderLeft:`2px solid ${color}` }}/>
      <div style={{ position:'absolute',bottom:-1,right:-1,width:8,height:8,borderBottom:`2px solid ${color}`,borderRight:`2px solid ${color}` }}/>
      {children}
    </div>
  )
}

export default function Admin() {
  const { user, profile } = useAuth()
  const [activeTab, setActiveTab] = useState('users')
  const [users, setUsers] = useState([])
  // UX-2 — full user list gets unruly at scale. Search trims by display
  // name / callsign / email; empty query collapses the list so the page
  // doesn't render hundreds of rows on every visit.
  const [userSearch, setUserSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(null)
  const [message, setMessage] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editCallsign, setEditCallsign] = useState('')
  const [editError, setEditError] = useState('')
  const [orgs, setOrgs] = useState([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [expandedOrg, setExpandedOrg] = useState(null)
  const [orgMembers, setOrgMembers] = useState({})
  const [orgMessage, setOrgMessage] = useState('')
  // Org IDs the current m55_admin user is already a staff member of —
  // drives the "+ ADD ME TO STAFF" button visibility on each card.
  const [myOrgMemberships, setMyOrgMemberships] = useState(new Set())
  const [addingMember, setAddingMember] = useState(null)
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('org_member')
  const [settings, setSettings] = useState([])
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState('')
  const [editingSetting, setEditingSetting] = useState(null)
  const [editingValue, setEditingValue] = useState('')
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgDescription, setNewOrgDescription] = useState('')
  const [newOrgOwnerEmail, setNewOrgOwnerEmail] = useState('')
  const [creatingOrg, setCreatingOrg] = useState(false)
  // Personnel tab — M55 platform staff hierarchy management.
  const [personnel, setPersonnel] = useState([])
  const [personnelLoading, setPersonnelLoading] = useState(false)
  const [personnelMessage, setPersonnelMessage] = useState('')
  const [newPersonnelEmail, setNewPersonnelEmail] = useState('')
  const [newPersonnelRole, setNewPersonnelRole] = useState('staff')

  useEffect(() => {
    if (!isPlatformAdmin(profile)) { window.location.href = '/dashboard'; return }
    fetchUsers()
  }, [profile])

  useEffect(() => {
    if (activeTab === 'orgs') fetchOrgs()
    if (activeTab === 'settings') fetchSettings()
    if (activeTab === 'personnel') fetchPersonnel()
  }, [activeTab])

  const fetchUsers = async () => { setLoading(true); const { data } = await supabase.from('users').select('*, player_stats(*)').order('created_at', { ascending: false }); setUsers(data || []); setLoading(false) }
  const startEdit = (u) => { setEditingId(u.id); setEditName(u.display_name || ''); setEditCallsign(u.callsign || ''); setEditError('') }
  const cancelEdit = () => { setEditingId(null); setEditName(''); setEditCallsign(''); setEditError('') }
  const saveEdit = async (userId) => {
    const nameError = validateDisplayName(editName); if (nameError) { setEditError(nameError); return }
    const callsignError = validateCallsign(editCallsign); if (callsignError) { setEditError(callsignError); return }
    setUpdating(userId)
    const { error } = await supabase.from('users').update({ display_name: editName.trim(), callsign: editCallsign.trim() || null }).eq('id', userId)
    if (error) { setEditError('Failed: ' + error.message) } else { setMessage('Updated'); cancelEdit(); fetchUsers() }
    setUpdating(null)
  }
  const updateRole = async (userId, newRole) => { setUpdating(userId); setMessage(''); const { error } = await supabase.from('users').update({ role: newRole }).eq('id', userId); if (error) { setMessage('Failed: ' + error.message) } else { setMessage('Role updated'); fetchUsers() }; setUpdating(null) }
  const deleteUser = async (userId, userEmail) => {
    if (userId === user?.id) { setMessage("Cannot delete own account"); return }
    if (!window.confirm(`Delete user ${userEmail}?`)) return
    const { error } = await supabase.rpc('admin_delete_user', { target_user_id: userId })
    if (error) { setMessage('Failed: ' + error.message) } else { setMessage('User deleted'); fetchUsers() }
  }
  const fetchOrgs = async () => {
    setOrgsLoading(true)
    // Fetch orgs and the current admin's existing memberships in parallel
    // so the "+ ADD ME" button can render correctly on first paint.
    const [orgsRes, myMembersRes] = await Promise.all([
      supabase.from('organizations').select('*, owner:users!organizations_owner_id_fkey(id, display_name, callsign, email)').order('created_at', { ascending: false }),
      user?.id
        ? supabase.from('org_members').select('org_id').eq('user_id', user.id)
        : Promise.resolve({ data: [] }),
    ])
    setOrgs(orgsRes.data || [])
    setMyOrgMemberships(new Set((myMembersRes.data || []).map(r => r.org_id)))
    setOrgsLoading(false)
  }

  // Self-add: m55_admin inserts an org_members row for themselves with
  // org_role='org_admin' so they show up with manage-staff privileges
  // alongside the org's owners. RLS already permits m55_admin INSERT.
  const addSelfToOrg = async (orgId) => {
    if (!user?.id) return
    const { error } = await supabase.from('org_members').insert({ org_id: orgId, user_id: user.id, org_role: 'org_admin' })
    if (error) {
      // Handle the duplicate-key race separately so we surface a clear msg.
      setOrgMessage(error.code === '23505'
        ? 'You are already on this org\'s staff'
        : 'Failed: ' + error.message)
      return
    }
    setOrgMessage('Added to staff')
    setMyOrgMemberships(prev => new Set([...prev, orgId]))
    // If the panel was open, refresh the visible staff list too.
    if (expandedOrg === orgId) fetchOrgMembers(orgId)
  }
  const fetchOrgMembers = async (orgId) => { const { data } = await supabase.from('org_members').select('*, user:users(id, display_name, callsign, email, platform_role)').eq('org_id', orgId); setOrgMembers(prev => ({ ...prev, [orgId]: data || [] })) }
  const toggleOrg = async (orgId) => { if (expandedOrg === orgId) { setExpandedOrg(null) } else { setExpandedOrg(orgId); if (!orgMembers[orgId]) await fetchOrgMembers(orgId) } }
  const transferOwnership = async (orgId, newOwnerId) => { if (!window.confirm('Transfer ownership?')) return; const { error } = await supabase.from('organizations').update({ owner_id: newOwnerId }).eq('id', orgId); if (error) { setOrgMessage('Failed: ' + error.message) } else { setOrgMessage('Transferred'); fetchOrgs() } }
  const addOrgMember = async (orgId) => {
    if (!newMemberEmail.trim()) return
    const { data: found } = await supabase.from('users').select('id, display_name, callsign, email').ilike('email', newMemberEmail.trim()).limit(1)
    if (!found?.[0]) { setOrgMessage('No user found'); return }
    const userId = found[0].id

    // Pre-check for existing org_members row to avoid duplicate inserts.
    // If the user is already on staff, offer to update their role instead.
    const { data: existing } = await supabase
      .from('org_members')
      .select('id, org_role')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .limit(1)

    if (existing?.[0]) {
      const current = existing[0].org_role
      const label = found[0].callsign || found[0].display_name || found[0].email
      if (current === newMemberRole) {
        setOrgMessage(`${label} is already on staff as ${current}`)
        return
      }
      if (!window.confirm(`${label} is already on staff as "${current}". Change their role to "${newMemberRole}"?`)) {
        setOrgMessage(`No change — ${label} stays as ${current}`)
        return
      }
      const { error: upErr } = await supabase
        .from('org_members')
        .update({ org_role: newMemberRole })
        .eq('id', existing[0].id)
      if (upErr) { setOrgMessage('Update failed: ' + upErr.message); return }
      setOrgMessage(`${label} role changed: ${current} → ${newMemberRole}`)
      setNewMemberEmail(''); setNewMemberRole('org_member'); setAddingMember(null); fetchOrgMembers(orgId)
      return
    }

    const { error } = await supabase.from('org_members').insert({ org_id: orgId, user_id: userId, org_role: newMemberRole })
    if (error) { setOrgMessage('Failed: ' + error.message) } else { setOrgMessage('Added'); setNewMemberEmail(''); setNewMemberRole('org_member'); setAddingMember(null); fetchOrgMembers(orgId) }
  }
  const removeOrgMember = async (orgId, memberId) => {
    if (!window.confirm('Remove member?')) return
    // Track 2.4 / Bug 50 — block-platform-staff trigger surfaces a
    // friendly message when an org-side admin tries to remove M55
    // platform staff. Same friendlyMessage path as elsewhere.
    const result = await mutate('org_members', () =>
      supabase.from('org_members').delete().eq('id', memberId)
    )
    if (result.error) {
      setOrgMessage(result.friendlyMessage || 'Remove failed.')
      return
    }
    setOrgMessage('Removed')
    fetchOrgMembers(orgId)
  }
  const generateSlug = (name) => name.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
  const createOrg = async () => {
    if (!newOrgName.trim()) return; setCreatingOrg(true)
    // Bug 39: platform staff are NOT the org's owner unless they explicitly
    // entered their own email. Resolve the designated owner first; if blank,
    // leave owner_id NULL and let the org sit owner-less until claimed.
    let ownerId = null
    if (newOrgOwnerEmail.trim()) {
      const { data: found } = await supabase.from('users').select('id').ilike('email', newOrgOwnerEmail.trim()).limit(1)
      if (!found?.[0]) { setOrgMessage('No user found'); setCreatingOrg(false); return }
      ownerId = found[0].id
    }
    const baseSlug = generateSlug(newOrgName); if (!baseSlug) { setOrgMessage('Invalid name'); setCreatingOrg(false); return }
    let slug = baseSlug; const { data: existing } = await supabase.from('organizations').select('slug').like('slug', `${baseSlug}%`)
    if (existing?.length) { const taken = new Set(existing.map(o => o.slug)); let c = 2; while (taken.has(slug)) { slug = `${baseSlug}-${c}`; c++ } }
    const { data: createdRows, error } = await supabase.from('organizations')
      .insert({ name: newOrgName.trim(), slug, description: newOrgDescription.trim() || null, owner_id: ownerId })
      .select('id').limit(1)
    if (error) { setOrgMessage('Failed: ' + error.message); setCreatingOrg(false); return }
    const newOrgId = createdRows?.[0]?.id
    // Seed org_members so the org isn't stranded:
    //   - designated owner gets org_owner
    //   - the staff creator gets org_admin (so they can keep managing it)
    //     unless they ARE the designated owner, in which case the owner row
    //     above already covers them.
    if (newOrgId) {
      const memberRows = []
      if (ownerId) memberRows.push({ org_id: newOrgId, user_id: ownerId, org_role: 'org_owner' })
      if (user.id !== ownerId) memberRows.push({ org_id: newOrgId, user_id: user.id, org_role: 'org_admin' })
      if (memberRows.length > 0) {
        await supabase.from('org_members').insert(memberRows)
      }
    }
    setOrgMessage('Created'); setNewOrgName(''); setNewOrgDescription(''); setNewOrgOwnerEmail(''); setShowCreateOrg(false); fetchOrgs()
    setCreatingOrg(false)
  }
  const fetchSettings = async () => { setSettingsLoading(true); const { data } = await supabase.from('m55_platform_settings').select('*').order('key'); setSettings(data || []); setSettingsLoading(false) }

  // ── PERSONNEL (M55 platform staff) ───────────────────────────
  const fetchPersonnel = async () => {
    setPersonnelLoading(true)
    const { data } = await supabase
      .from('users')
      .select('id, email, display_name, callsign, platform_role')
      .not('platform_role', 'is', null)
      .order('platform_role', { ascending: true })
    setPersonnel(data || [])
    setPersonnelLoading(false)
  }
  const addPersonnel = async () => {
    if (!newPersonnelEmail.trim()) return
    const { data: found } = await supabase
      .from('users').select('id, display_name, callsign, email, platform_role')
      .ilike('email', newPersonnelEmail.trim()).limit(1)
    if (!found?.[0]) { setPersonnelMessage('No user found'); return }
    if (found[0].platform_role) {
      setPersonnelMessage(`${found[0].callsign || found[0].email} is already platform ${found[0].platform_role}`)
      return
    }
    const { error } = await supabase
      .from('users').update({ platform_role: newPersonnelRole }).eq('id', found[0].id)
    if (error) { setPersonnelMessage('Failed: ' + error.message); return }
    setPersonnelMessage(`${found[0].callsign || found[0].email} added as ${newPersonnelRole}`)
    setNewPersonnelEmail(''); setNewPersonnelRole('staff')
    fetchPersonnel()
  }
  const updatePersonnelRole = async (userId, newRole) => {
    const { error } = await supabase.from('users').update({ platform_role: newRole }).eq('id', userId)
    if (error) setPersonnelMessage('Failed: ' + error.message)
    else { setPersonnelMessage('Platform role updated'); fetchPersonnel() }
  }
  const removePersonnel = async (userId, label) => {
    if (userId === user?.id) { setPersonnelMessage('Cannot remove yourself'); return }
    if (!window.confirm(`Remove platform-staff status from ${label}?`)) return
    const { error } = await supabase.from('users').update({ platform_role: null }).eq('id', userId)
    if (error) setPersonnelMessage('Failed: ' + error.message)
    else { setPersonnelMessage('Removed'); fetchPersonnel() }
  }
  const updateSetting = async (key) => { const { error } = await supabase.from('m55_platform_settings').update({ value: editingValue, updated_at: new Date().toISOString() }).eq('key', key); if (error) { setSettingsMessage('Failed: ' + error.message) } else { setSettingsMessage('Updated'); setEditingSetting(null); setEditingValue(''); fetchSettings() } }

  const tabs = [
    { id: 'users', label: 'USERS', icon: Users },
    { id: 'personnel', label: 'PERSONNEL', icon: UserCog },
    { id: 'orgs', label: 'ORGS', icon: Building2 },
    { id: 'settings', label: 'SETTINGS', icon: Settings },
  ]

  return (
    <div style={C.page}>
      <style>{`@keyframes cipher-blink { 50% { opacity: 0; } }`}</style>

      {/* ── NAV ── */}
      <nav style={C.nav}>
        <div className="flex items-center gap-4">
          <button onClick={() => window.location.href = '/dashboard'} style={C.btnNav}>
            <ArrowLeft size={14} /> BACK
          </button>
          <span style={{ ...C.display, fontSize: '16px', color: '#00ff66', textShadow: '0 0 6px rgba(0,255,102,.6)' }}>
            {'>_'} M55 ADMIN
          </span>
        </div>
        <button onClick={() => {
          if (activeTab === 'users') fetchUsers()
          else if (activeTab === 'orgs') fetchOrgs()
          else if (activeTab === 'personnel') fetchPersonnel()
          else fetchSettings()
        }} style={C.btnNav}>
          <RefreshCw size={14} />
        </button>
      </nav>

      {/* ── TABS ── */}
      <div style={{ background: '#0a140a', borderBottom: '1px solid rgba(0,255,102,.18)', padding: '0 16px', display: 'flex', gap: '2px', position: 'relative', zIndex: 10 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex items-center gap-2 px-4 py-3 text-sm transition-colors"
            style={{
              ...C.display,
              fontSize: '13px',
              color: activeTab === t.id ? '#00ff66' : '#2a6a3a',
              borderBottom: activeTab === t.id ? '2px solid #00ff66' : '2px solid transparent',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === t.id ? '2px solid #00ff66' : '2px solid transparent',
              cursor: 'pointer',
              textShadow: activeTab === t.id ? '0 0 6px rgba(0,255,102,.6)' : 'none',
            }}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <main className="max-w-4xl mx-auto p-6" style={{ position: 'relative', zIndex: 10 }}>

        {/* ── USERS TAB ── */}
        {activeTab === 'users' && (
          <>
            <h2 className="text-xl font-bold mb-1" style={C.display}>
              <Users size={20} className="inline mr-2" style={{ verticalAlign: 'middle' }} />
              USER REGISTRY
            </h2>
            <p className="text-sm mb-3" style={C.dim}>{users.length} records</p>

            {/* Search bar — name / callsign / email. Empty query collapses
                the list so the page doesn't render the whole user table on
                every visit. */}
            <div className="mb-4">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name, callsign, or email..."
                maxLength={100}
                style={{ ...C.input, width: '100%' }}
              />
              {userSearch.trim() && (
                <p className="text-xs mt-1" style={C.deep}>
                  {users.filter(u => {
                    const q = userSearch.trim().toLowerCase()
                    return (u.display_name || '').toLowerCase().includes(q)
                      || (u.callsign || '').toLowerCase().includes(q)
                      || (u.email || '').toLowerCase().includes(q)
                  }).length} match{users.filter(u => {
                    const q = userSearch.trim().toLowerCase()
                    return (u.display_name || '').toLowerCase().includes(q)
                      || (u.callsign || '').toLowerCase().includes(q)
                      || (u.email || '').toLowerCase().includes(q)
                  }).length === 1 ? '' : 'es'}
                </p>
              )}
            </div>

            {message && (
              <div className="mb-4 p-3 text-sm" style={{
                ...C.panel,
                color: message.includes('Failed') || message.includes('Cannot') ? '#ff2a2a' : '#00ff66',
                borderColor: message.includes('Failed') || message.includes('Cannot') ? 'rgba(255,42,42,.3)' : 'rgba(0,255,102,.3)',
              }}>{message}</div>
            )}

            {loading ? (
              <p style={C.dim}>Loading<span style={{ display: 'inline-block', width: 8, height: 14, background: '#00ff66', boxShadow: '0 0 6px #00ff66', verticalAlign: 'middle', marginLeft: 4, animation: 'cipher-blink 1s steps(2) infinite' }} /></p>
            ) : (() => {
              const q = userSearch.trim().toLowerCase()
              const filteredUsers = q
                ? users.filter(u =>
                    (u.display_name || '').toLowerCase().includes(q)
                    || (u.callsign || '').toLowerCase().includes(q)
                    || (u.email || '').toLowerCase().includes(q))
                : users
              if (!q && users.length > 20) {
                // Without a query, surface the prompt rather than the wall
                // of rows. Power users searching by callsign get instant
                // feedback; a tester opening the panel sees a hint, not
                // 200 cards.
                return (
                  <p className="text-center py-8" style={{ ...C.dim, fontSize: 13 }}>
                    {users.length} users on file — start typing to search.
                  </p>
                )
              }
              if (filteredUsers.length === 0) {
                return (
                  <p className="text-center py-8" style={{ ...C.dim, fontSize: 13 }}>
                    No matches for "{userSearch.trim()}".
                  </p>
                )
              }
              return (
              <div className="space-y-2">
                {filteredUsers.map((u, i) => (
                  <Brackets key={u.id} className="p-4" style={{ marginBottom: '8px' }}>
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold"
                          style={{ background: '#00ff66', color: '#000', textShadow: 'none', fontFamily: "'VT323', monospace", fontSize: '18px' }}>
                          {(u.display_name || u.email || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingId === u.id ? (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Display name" maxLength={32} style={{ ...C.input, width: '160px' }} />
                                <input type="text" value={editCallsign} onChange={(e) => setEditCallsign(e.target.value)} placeholder="Callsign" maxLength={20} style={{ ...C.input, width: '128px' }} />
                              </div>
                              {editError && <p className="text-xs" style={C.alert}>{editError}</p>}
                              <div className="flex gap-2">
                                <button onClick={() => saveEdit(u.id)} disabled={updating === u.id} style={C.btnPrimary}><Check size={12} /> SAVE</button>
                                <button onClick={cancelEdit} style={C.btnSecondary}><X size={12} /> CANCEL</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium" style={{ ...C.display, fontSize: '14px' }}>{u.display_name || u.email}</p>
                                {u.callsign && <span className="text-sm" style={C.dim}>"{u.callsign}"</span>}
                                <span style={{ ...C.tag, ...roleTag(u.role) }}>{roleLabel(u.role)}</span>
                                {u.id !== user.id && (
                                  <button onClick={() => startEdit(u)} style={{ color: '#2a6a3a', background: 'none', border: 'none', cursor: 'pointer' }}><Pencil size={13} /></button>
                                )}
                              </div>
                              <p className="text-sm" style={C.dim}>{u.email}</p>
                              <p className="text-xs mt-0.5" style={C.deep}>
                                {u.player_stats?.total_points || 0} pts · {u.player_stats?.bounties_claimed || 0} claimed · {u.player_stats?.kills_recorded || 0} kills
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      {u.id !== user.id && editingId !== u.id && (
                        <div className="flex items-center gap-2 ml-3">
                          <select value={u.role} onChange={(e) => updateRole(u.id, e.target.value)} disabled={updating === u.id} style={C.select}>
                            <option value="player">Player</option>
                            <option value="gm">GM</option>
                            <option value="m55_admin">Admin</option>
                          </select>
                          <button onClick={() => deleteUser(u.id, u.email)} disabled={updating === u.id} style={C.btnDanger}>DEL</button>
                        </div>
                      )}
                      {u.id === user.id && <span className="text-xs ml-3" style={C.deep}>YOU</span>}
                    </div>
                  </Brackets>
                ))}
              </div>
              )
            })()}
          </>
        )}

        {/* ── PERSONNEL TAB ── */}
        {activeTab === 'personnel' && (
          <>
            <h2 className="text-xl font-bold mb-1" style={C.display}>
              <UserCog size={20} className="inline mr-2" style={{ verticalAlign: 'middle' }} />
              M55 PERSONNEL
            </h2>
            <p className="text-sm mb-6" style={C.dim}>
              {personnel.length} platform staff — Tier 1 (god-mode across all orgs and events)
            </p>

            {personnelMessage && (
              <div className="mb-4 p-3 text-sm" style={{
                ...C.panel,
                color: personnelMessage.includes('Failed') || personnelMessage.includes('Cannot') ? '#ff2a2a' : '#00ff66',
                borderColor: personnelMessage.includes('Failed') || personnelMessage.includes('Cannot') ? 'rgba(255,42,42,.3)' : 'rgba(0,255,102,.3)',
              }}>{personnelMessage}</div>
            )}

            {/* Add personnel form */}
            <div style={{ ...C.panel, padding: 16, marginBottom: 16 }}>
              <p className="text-xs mb-2" style={{ ...C.dim, letterSpacing: '0.1em', textTransform: 'uppercase' }}>ADD PLATFORM STAFF</p>
              <div className="flex gap-2 flex-wrap">
                <input type="email" value={newPersonnelEmail} onChange={e => setNewPersonnelEmail(e.target.value)}
                  placeholder="user@email.com" style={{ ...C.input, flex: 1, minWidth: 200 }} />
                <select value={newPersonnelRole} onChange={e => setNewPersonnelRole(e.target.value)} style={C.select}>
                  <option value="staff">staff</option>
                  <option value="admin">admin</option>
                  <option value="support">support</option>
                </select>
                <button onClick={addPersonnel} style={C.btnPrimary}><Plus size={12} /> ADD</button>
              </div>
            </div>

            {personnelLoading ? (
              <p style={C.dim}>Loading personnel…</p>
            ) : personnel.length === 0 ? (
              <p style={C.dim}>No platform staff seeded. Add yourself or another admin above.</p>
            ) : (
              <div className="space-y-2">
                {personnel.map(p => {
                  const label = p.callsign || p.display_name || p.email
                  return (
                    <Brackets key={p.id} className="p-3" style={{ marginBottom: 6 }}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm" style={{ ...C.display }}>{label}</p>
                          <p className="text-xs" style={C.deep}>{p.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <select value={p.platform_role}
                            onChange={e => updatePersonnelRole(p.id, e.target.value)}
                            disabled={p.id === user?.id}
                            style={C.select}>
                            <option value="staff">staff</option>
                            <option value="admin">admin</option>
                            <option value="support">support</option>
                          </select>
                          <button onClick={() => removePersonnel(p.id, label)}
                            disabled={p.id === user?.id}
                            style={{ ...C.btnNav, color: p.id === user?.id ? '#2a6a3a' : '#ff7a18', cursor: p.id === user?.id ? 'not-allowed' : 'pointer' }}
                            title={p.id === user?.id ? 'Cannot remove yourself' : 'Remove platform staff'}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </Brackets>
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* ── ORGS TAB ── */}
        {activeTab === 'orgs' && (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold" style={C.display}>
                <Building2 size={20} className="inline mr-2" style={{ verticalAlign: 'middle' }} />
                ORGANIZATIONS
              </h2>
              <button onClick={() => setShowCreateOrg(!showCreateOrg)} style={C.btnPrimary}><Plus size={14} /> NEW ORG</button>
            </div>
            <p className="text-sm mb-6" style={C.dim}>{orgs.length} records</p>

            {showCreateOrg && (
              <Brackets bright className="p-4 mb-6 space-y-3">
                <p style={{ ...C.display, fontSize: '13px' }}>CREATE ORGANIZATION</p>
                <input type="text" placeholder="Organization name" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} style={{ ...C.input, width: '100%' }} maxLength={80} />
                <input type="text" placeholder="Description (optional)" value={newOrgDescription} onChange={(e) => setNewOrgDescription(e.target.value)} style={{ ...C.input, width: '100%' }} maxLength={120} />
                <input type="email" placeholder="Owner email (blank = self)" value={newOrgOwnerEmail} onChange={(e) => setNewOrgOwnerEmail(e.target.value)} style={{ ...C.input, width: '100%' }} maxLength={100} />
                <div className="flex gap-2">
                  <button onClick={createOrg} disabled={creatingOrg || !newOrgName.trim()} style={{ ...C.btnPrimary, opacity: creatingOrg ? 0.5 : 1 }}>{creatingOrg ? 'CREATING...' : 'CREATE'}</button>
                  <button onClick={() => setShowCreateOrg(false)} style={C.btnSecondary}>CANCEL</button>
                </div>
              </Brackets>
            )}

            {orgMessage && (
              <div className="mb-4 p-3 text-sm" style={{ ...C.panel, color: orgMessage.includes('Failed') || orgMessage.includes('No user') ? '#ff2a2a' : '#00ff66' }}>{orgMessage}</div>
            )}

            {orgsLoading ? <p style={C.dim}>Loading...</p> : orgs.length === 0 ? <p className="text-center py-12" style={C.deep}>NO ORGANIZATIONS</p> : (
              <div className="space-y-2">
                {orgs.map(org => {
                  const isExpanded = expandedOrg === org.id
                  const members = orgMembers[org.id] || []
                  const ownerName = org.owner?.callsign || org.owner?.display_name || org.owner?.email || 'Unknown'
                  return (
                    <Brackets key={org.id} className="overflow-hidden">
                      <div className="w-full flex items-center justify-between p-4" style={{ color: '#00ff66', textAlign: 'left', textShadow: '0 0 4px rgba(0,255,102,.5)' }}>
                        <button onClick={() => toggleOrg(org.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', textShadow: 'inherit' }}>
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold" style={{ background: '#00ff66', color: '#000', textShadow: 'none', fontFamily: "'VT323', monospace", fontSize: '18px' }}>
                            {org.name[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate" style={C.display}>{org.name}</p>
                            <p className="text-xs" style={C.dim}>Owner: {ownerName}</p>
                            {org.description && <p className="text-xs mt-0.5 truncate" style={C.deep}>{org.description}</p>}
                          </div>
                        </button>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {!myOrgMemberships.has(org.id) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); addSelfToOrg(org.id) }}
                              style={{ ...C.btnNav, fontSize: '11px' }}
                              title="Add yourself to this org's staff"
                            >
                              <Plus size={12} /> ADD ME TO STAFF
                            </button>
                          )}
                          <button onClick={() => toggleOrg(org.id)} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer' }} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="p-4 space-y-4" style={{ borderTop: '1px solid rgba(0,255,102,.12)' }}>
                          <div>
                            <p className="text-xs mb-2" style={{ ...C.deep, letterSpacing: '0.12em', textTransform: 'uppercase' }}>OWNERSHIP TRANSFER</p>
                            <select style={{ ...C.select, width: '100%' }} defaultValue="" onChange={(e) => e.target.value && transferOwnership(org.id, e.target.value)}>
                              <option value="">Transfer to user...</option>
                              {users.filter(u => u.id !== org.owner_id).map(u => (<option key={u.id} value={u.id}>{u.callsign || u.display_name || u.email}</option>))}
                            </select>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs" style={{ ...C.deep, letterSpacing: '0.12em', textTransform: 'uppercase' }}>STAFF ({members.length})</p>
                              <button onClick={() => setAddingMember(addingMember === org.id ? null : org.id)} style={{ ...C.btnNav, fontSize: '11px' }}><Plus size={12} /> ADD</button>
                            </div>
                            {addingMember === org.id && (
                              <div className="flex gap-2 mb-3">
                                <input type="email" value={newMemberEmail} onChange={(e) => setNewMemberEmail(e.target.value)} placeholder="User email" style={{ ...C.input, flex: 1 }} />
                                <select value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)} style={C.select}>
                                  {ORG_ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                                <button onClick={() => addOrgMember(org.id)} style={C.btnPrimary}>ADD</button>
                              </div>
                            )}
                            {members.length === 0 ? <p className="text-sm" style={C.deep}>No staff</p> : (
                              <div className="space-y-1">
                                {members.map(m => (
                                  <div key={m.id} className="flex items-center justify-between px-3 py-2" style={{ background: 'rgba(0,20,8,.3)' }}>
                                    <div>
                                      <p className="text-sm">{m.user?.callsign || m.user?.display_name || m.user?.email}</p>
                                      <p className="text-xs" style={C.deep}>{m.user?.email}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span style={{ ...C.tag, ...orgRoleTag(m.org_role) }}>{m.org_role}</span>
                                      {!m.user?.platform_role && (
                                        <button onClick={() => removeOrgMember(org.id, m.id)} style={{ color: '#2a6a3a', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={13} /></button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Brackets>
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === 'settings' && (
          <>
            <h2 className="text-xl font-bold mb-1" style={C.display}>
              <Settings size={20} className="inline mr-2" style={{ verticalAlign: 'middle' }} />
              PLATFORM SETTINGS
            </h2>
            <p className="text-sm mb-6" style={C.dim}>Global toggles for Anubis mechanics</p>

            {settingsMessage && (
              <div className="mb-4 p-3 text-sm" style={{ ...C.panel, color: '#ffcc33' }}>{settingsMessage}</div>
            )}

            {settingsLoading ? <p style={C.dim}>Loading...</p> : settings.length === 0 ? (
              <Brackets className="p-6 text-center">
                <p style={{ ...C.display, fontSize: 14, color: '#4aaa5a' }}>NO SETTINGS CONFIGURED</p>
                <p className="text-xs mt-2" style={C.deep}>
                  Platform settings are seeded by migration. Run the latest
                  m55_platform_settings seed migration, or insert a row via
                  SQL editor: <span style={{ ...C.mono, color: '#ffcc33' }}>INSERT INTO m55_platform_settings (key, value, description) VALUES (...);</span>
                </p>
              </Brackets>
            ) : (
              <div className="space-y-2">
                {settings.map(setting => (
                  <Brackets key={setting.key} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium" style={{ ...C.mono, color: '#9aff9a' }}>{setting.key}</p>
                        {setting.description && <p className="text-xs mt-1" style={C.dim}>{setting.description}</p>}
                        {editingSetting === setting.key ? (
                          <div className="flex items-center gap-2 mt-2">
                            <input type="text" value={editingValue} onChange={(e) => setEditingValue(e.target.value)} style={{ ...C.input, flex: 1 }} maxLength={120} autoFocus />
                            <button onClick={() => updateSetting(setting.key)} style={{ color: '#00ff66', background: 'none', border: 'none', cursor: 'pointer' }}><Check size={16} /></button>
                            <button onClick={() => { setEditingSetting(null); setEditingValue('') }} style={{ color: '#2a6a3a', background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
                          </div>
                        ) : (
                          <p className="text-sm mt-1" style={{ ...C.mono, color: '#ffcc33' }}>{setting.value}</p>
                        )}
                      </div>
                      {editingSetting !== setting.key && (
                        <button onClick={() => { setEditingSetting(setting.key); setEditingValue(setting.value) }} style={{ color: '#2a6a3a', background: 'none', border: 'none', cursor: 'pointer' }}><Pencil size={14} /></button>
                      )}
                    </div>
                  </Brackets>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
